import { deliverySummary, tripSummary } from '../../../utils/tripLabels.js';
import type { ScheduleBar } from '../types.js';
import { barStyleFor } from './barStyle.js';
import { mytPeriod } from './dropLogic.js';

/** The short booking reference used across the admin (`BookingListPage`, `BookingDetailPage`). */
export const shortRef = (id: string) => id.slice(-8);

export const isBookingBar = (bar: ScheduleBar) =>
  bar.kind === 'booking' || bar.kind === 'extension';

/**
 * 316 (FR-019) — everything about a bar, on hover or focus: customer, reference, car, exact times,
 * status and payment. The customer line exists only when the server sent `customer_name`, which it
 * does for staff who manage bookings (Admin) and never for a Supplier or Host (FR-018).
 */
export function ScheduleBarCard({ bar, carLabel }: { bar: ScheduleBar; carLabel: string }) {
  const style = barStyleFor(bar);
  const booking = isBookingBar(bar);
  return (
    <div
      data-testid="schedule-bar-card"
      className="flex max-w-72 flex-col gap-0.5 text-xs text-ink"
    >
      {bar.customer_name && (
        <p data-testid="schedule-bar-card-customer" className="font-semibold">
          {bar.customer_name}
        </p>
      )}
      {booking && bar.booking_id && <p>Booking {shortRef(bar.booking_id)}</p>}
      {bar.root_booking_id && <p>Extension of booking {shortRef(bar.root_booking_id)}</p>}
      <p className="text-steel">{carLabel}</p>
      <p>{mytPeriod(Date.parse(bar.start_at), Date.parse(bar.end_at))}</p>
      <p>
        {style.word}
        {bar.overdue ? ' · Overdue' : ''}
      </p>
      {booking && bar.money_settled !== undefined && (
        <p>{bar.money_settled ? 'Paid' : 'Not paid'}</p>
      )}
      {/* 361: the customer's trip answers, in words. */}
      {bar.trip && (
        <>
          <p data-testid="schedule-bar-card-trip">{tripSummary(bar.trip)}</p>
          <p data-testid="schedule-bar-card-delivery">{deliverySummary(bar.trip)}</p>
        </>
      )}
      {bar.reason && <p className="text-steel">{bar.reason}</p>}
    </div>
  );
}
